import { FC, useCallback, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  confirmPanelFormClose,
  Form,
  FormAlerts,
  FormField,
  FormSelect,
} from '../../../common/form';
import { useFormState } from 'react-hook-form';
import { Padding } from '../../../common/components/Padding';
import validationTranslations from '../../translations/validation.translations.json';
import {
  ActionButton,
  getPeriod,
  getPrevPeriodEnd,
  TextEllipsis,
} from '../../../common/components';
import translations from './AlgorithmTestCalculateModal.translations.json';
import {
  useSystemClassifierItemsBy,
  useSystemClassifierSelectOptions,
} from '../../../configuration';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { getDayjs, getDayjsNow } from '../../../common/utils';
import { Frequency } from '../../types';
import { Dayjs } from 'dayjs';
import { TextLink } from '../../../common/components/typography';
import { FormPeriodEndPicker } from '../../../common/form/input/FormPeriodEndPicker';
import { AlgorithmTestCalculationType } from './schema/types';
import { AlgorithmTestCalculationValidFormDataSchema } from './schema/AlgorithmTestCalculationSchema';
import { isDisabledDate } from '../../valueDate/utils';

const { addTranslations, t } = i18nNsFactory('algorithm-test-run-modal');
addTranslations(translations);
addTranslations(validationTranslations);

export interface TestRunAlgorithmsModalProps {
  algorithms: { id: number; code: string }[];
  onClose: () => void;
}

export const TestCalculateAlgorithmsModal: FC<TestRunAlgorithmsModalProps> = (
  props,
) => {
  const { algorithms, onClose } = props;

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const [openTestCalculate] =
    DataObjectDescriptionVersionStore.useValue('openTestCalculate');

  const systemClassifierItemsById = useSystemClassifierItemsBy(['FREQUENCY']);
  const frequencyCode = (systemClassifierItemsById['FREQUENCY'].get(
    dataObjectDescriptionVersion?.frequencyId ?? 0,
  )?.code ?? 'WEEK') as Frequency;

  const versionValidTo = getDayjs(dataObjectDescriptionVersion?.validPeriod[1]);
  const previousPeriodEnd = getPrevPeriodEnd(getDayjsNow(), frequencyCode);

  const validPeriod = [
    getDayjs(dataObjectDescriptionVersion?.validPeriod[0]),
    versionValidTo?.isBefore(previousPeriodEnd)
      ? versionValidTo
      : previousPeriodEnd,
  ] as [Dayjs | null, Dayjs | null];

  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION'], 'id');

  const handleSubmit = useCallback(
    (params: AlgorithmTestCalculationType) => {
      onClose();
      openTestCalculate?.(params);
    },
    [openTestCalculate, onClose],
  );

  const defaultValues = useMemo(
    () => ({
      algorithmIds: algorithms.map((alg) => alg.id),
      sourceDataVersion: sourceDataVersionOptions.find(
        (option) => option.code === 'LATEST',
      )?.value as number | undefined,
    }),
    [algorithms, sourceDataVersionOptions],
  );

  return (
    <Form
      schema={AlgorithmTestCalculationValidFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      idAttributePrefix="algorithmTestRun"
    >
      <TestRunAlgorithmsModalInner
        algorithms={algorithms}
        validPeriod={validPeriod}
        frequency={frequencyCode}
        onClose={onClose}
      />
    </Form>
  );
};

function TestRunAlgorithmsModalInner({
  algorithms,
  validPeriod,
  frequency,
  onClose,
}: {
  algorithms: { id: number; code: string }[];
  validPeriod: [Dayjs | null, Dayjs | null];
  frequency: Frequency;
  onClose?: () => void;
}) {
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  const [isClipped, setIsClipped] = useState(false);
  const [showAllData, setShowAllData] = useState(false);

  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);

  const algorithmData = (
    <>
      <strong>
        {t('label.algorithms')} ({algorithms.length}):{' '}
      </strong>{' '}
      {algorithms.map((alg) => alg.code).join(', ')}
    </>
  );

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton
              type="abort"
              onClick={handleCancel}
              label={t('button.cancel')}
            />
            <ActionButton type="formSubmit" label={t('button.run')} />
          </Flex>
        </ModalFooter>
      }
      onCancel={onClose}
      width="30rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`heading`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="middle">
          <div>
            {showAllData ? (
              <div>{algorithmData}</div>
            ) : (
              <TextEllipsis
                rows={2}
                onIsClipped={setIsClipped}
                renderTooltip={false}
              >
                {algorithmData}
              </TextEllipsis>
            )}
            {isClipped && !showAllData && (
              <TextLink id={`read-more`} onClick={() => setShowAllData(true)}>
                {t('button.readMore')}
              </TextLink>
            )}
            {showAllData && (
              <TextLink id={`show-less`} onClick={() => setShowAllData(false)}>
                {t('button.showLess')}
              </TextLink>
            )}
          </div>
          <FormField
            name="valueDate"
            size="small"
            label={t('fields.valueDate.label')}
            required
          >
            <FormPeriodEndPicker
              name="valueDate"
              frequency={frequency}
              disabledDate={(date) =>
                isDisabledDate(getPeriod(date, frequency)[1]!, [validPeriod])
              }
            />
          </FormField>
          <FormField
            name="sourceDataVersionId"
            label={t('fields.sourceDataVersion.label')}
            required
          >
            <FormSelect
              name="sourceDataVersionId"
              options={sourceDataVersionOptions}
            />
          </FormField>
        </Flex>
      </Padding>
    </Modal>
  );
}
